/* import __COLOCATED_TEMPLATE__ from './permissions.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { taskFor } from 'ember-concurrency-ts';
import { action } from '@ember/object';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type IntlService from 'ember-intl/services/intl';
import { type SalesforceSetupData } from 'embercom/objects/standalone/salesforce';

interface Args {}

export default class StandaloneSalesforcePermissions extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    if (this.finStandaloneService.salesforceSetupData.hasPermissionSet === undefined) {
      taskFor(this.finStandaloneService.checkSalesforcePermissions).perform();
    }
  }

  get salesforceConfig(): SalesforceConfiguration {
    return this.finStandaloneService.salesforceConfig!;
  }

  get salesforceSetupData(): SalesforceSetupData {
    return this.finStandaloneService.salesforceSetupData;
  }

  get hasFinPermissions() {
    return this.salesforceSetupData.hasPermissionSetAssignedToInstaller;
  }

  get isPermissionPackageInstalled() {
    return this.salesforceSetupData.hasPermissionSet;
  }

  get isLoading() {
    return this.finStandaloneService.isLoadingSalesforcePermissions;
  }

  @action
  openUrl() {
    safeWindowOpen(this.finRequiredPermissionsUrl, '_blank');
  }

  get finRequiredPermissionsUrl() {
    return this.salesforceConfig.finRequiredPermissionsUrl;
  }

  get installationSteps() {
    return [
      {
        label: this.intl.t('standalone.salesforce.setup.fin-permissions.setup-step-1'),
      },
      {
        label: this.intl.t('standalone.salesforce.setup.fin-permissions.setup-step-2', {
          htmlSafe: true,
        }),
      },
      {
        label: this.intl.t('standalone.salesforce.setup.fin-permissions.setup-step-3', {
          htmlSafe: true,
        }),
      },
      {
        label: this.intl.t('standalone.salesforce.setup.fin-permissions.setup-step-4'),
      },
      {
        label: this.intl.t('standalone.salesforce.setup.fin-permissions.setup-step-5', {
          htmlSafe: true,
        }),
      },
    ];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::Setup::Permissions': typeof StandaloneSalesforcePermissions;
    'standalone/salesforce/setup/permissions': typeof StandaloneSalesforcePermissions;
  }
}
